import { useEffect, useMemo, useState } from "react";
import { useGetTraitList } from "../../../../net/CharacterFeatureManagement";
import { useWindowDimensions } from "../../../../common/utilities/WindowDimensions";
import { TableSort } from "../../../../common/components/layout/Table/Table";
import Cookies from "universal-cookie";
import FlexRow from "../../../../common/components/layout/Flexbox/FlexRow";
import Modal from "../../../../common/components/modals/Modal/Modal";
import Table from "../../../../common/components/layout/Table/Table";
import TextField from "../../../../common/components/display/TextField/TextField";
import TextInput from "../../../../common/components/input/TextInput/TextInput";
import Trait from "../../../../utils/game/mechanics/Trait";
import TraitSorter from "../../../../common/utilities/sorters/TraitSorter";

import "../style/CommonModalStyle_style.css";

type Props = {
    isVisible?: boolean;
    traitOptions?: Array<Trait>;
    /** Filters trait options based on external properties */
    optionFilter?: ((t: Trait) => boolean) | null;
    onTraitOptionsChanged?: (traits: Array<Trait>) => void;
    onClose?: () => void;
    onConfirm?: (traits: Array<Trait>) => void;
};

export default function LoadTraitModal(props: Props): JSX.Element {
    const [loadTraitList, isLoadingTraitList] = useGetTraitList();
    const [selectedTraitIDs, setSelectedTraitIDs] = useState<Array<number>>([]);
    const [traitList, setTraitList] = useState<Array<Trait>>(
        props.traitOptions ?? []
    );
    const [traitListSort, setTraitListSort] =
        useState<TableSort | null>(null);
    const [searchStr, setSearchStr] = useState<string>("");

    const traitSorter = useMemo(() => {
        return TraitSorter.createSorter(traitListSort?.key, traitListSort?.order);}, [traitListSort]);

    const cookies = new Cookies();

    const windowDimensions = useWindowDimensions();

    useEffect(() => {
        if (props.traitOptions === undefined) {
            loadTraitList({
                variables: { token: cookies.get("session_token") },
                onComplete: (result) => {
                    setTraitList(result);
                    props.onTraitOptionsChanged?.(result);
                },
                onError: (error) => {},
            });
        }
    }, []);

    const tableFilter = useMemo(() => {
        const searchFilter =
            searchStr.length > 0
                ? (trait: Trait) => {
                      const match = new RegExp(searchStr, "i");
                      const result =
                          trait.title.match(match) ||
                          trait.subtype.match(match) ||
                          trait.description.match(match);

                      return (
                          (props.optionFilter == null ||
                              props.optionFilter(trait)) &&
                          result !== null
                      );
                  }
                : null;

        const filters = [props.optionFilter, searchFilter].flatMap(
            (f) => f ?? []
        );
        return filters.length > 0
            ? (trait: Trait) => filters.every((filter) => filter(trait))
            : null;
    }, [props.optionFilter, searchStr]);

    return (
        <Modal
            contentClassName="character-common-modal"
            header="Add Trait"
            isVisible={props.isVisible}
            width={windowDimensions.width >= 900 ? 750 : 400}
            footer="standard-submit-cancel"
            onClose={props.onClose}
            onConfirm={() => {
                props.onConfirm?.(
                    traitList.filter((trait) =>
                        selectedTraitIDs.includes(trait.id)
                    )
                );
            }}
        >
            <FlexRow>
                <FlexRow>
                    <TextField
                        type="bold"
                        style={{ width: "auto", margin: "8px" }}
                    >
                        Search
                    </TextField>
                    <TextInput
                        value={searchStr}
                        style={{
                            width: `${
                                windowDimensions.width >= 900
                                    ? "240px"
                                    : "120px"
                            }`,
                        }}
                        onChange={setSearchStr}
                    />
                </FlexRow>
                <FlexRow alignment="end">
                    <TextField
                        type="bold"
                        style={{ width: "auto", margin: "8px" }}
                    >{`${selectedTraitIDs.length} Selected`}</TextField>
                    <button
                        onClick={() => {
                            setSelectedTraitIDs([]);
                        }}
                    >
                        Clear
                    </button>
                </FlexRow>
            </FlexRow>
            <hr />
            <Table
                className="options-list"
                columns={[
                    {
                        columnKey: "title",
                        columnHeader: "Title",
                        sortable: true,
                        width: "25%",
                    },
                    {
                        columnKey: "subtype",
                        columnHeader: "Subtype",
                        sortable: true,
                        width: "20%",
                    },
                    {
                        columnKey: "cost",
                        columnHeader: "Cost",
                        sortable: true,
                        width: "15%",
                    },
                    {
                        columnKey: "description",
                        columnHeader: "Description",
                        sortable: true,
                        width: "40%",
                    },
                ]}
                rowData={traitList}
                cellParser={[
                    {
                        columnKey: "title",
                        callback: (trait) => {
                            return (
                                <TextField className="option-item" numberOfLines={3}>
                                    {trait.title}
                                </TextField>
                            );
                        },
                    },
                    {
                        columnKey: "subtype",
                        callback: (trait) => {
                            return (
                                <TextField className="option-item">
                                    {trait.subtype}
                                </TextField>
                            );
                        },
                    },
                    {
                        columnKey: "cost",
                        callback: (trait) => {
                            return (
                                <TextField className="option-item">
                                    {trait.cost.toString()}
                                </TextField>
                            );
                        },
                    },
                    {
                        columnKey: "description",
                        callback: (trait) => {
                            return (
                                <TextField
                                    className="option-item"
                                    numberOfLines={15}
                                >
                                    {trait.description}
                                </TextField>
                            );
                        },
                    },
                ]}
                idGetter={(trait) => trait.id.toString()}
                sort={traitListSort}
                sorter={traitSorter}
                onChangeSort={setTraitListSort}
                isRowSelected={(trait) => selectedTraitIDs.includes(trait.id)}
                onRowClick={(trait) => {
                    if (selectedTraitIDs.includes(trait.id)) {
                        setSelectedTraitIDs([
                            ...selectedTraitIDs.filter(
                                (entry) => entry !== trait.id
                            ),
                        ]);
                    } else {
                        setSelectedTraitIDs([...selectedTraitIDs, trait.id]);
                    }
                }}
                rowFilter={tableFilter}
                showHeader={true}
            />
        </Modal>
    );
}
